import React from "react";
import { Check } from "lucide-react";
import cityPageContent from "../data/city-page-content";

/**
 * Main body column for cities with their own copy (Figma node 428:823).
 *
 * Replaces the shared Contentful `content` HTML, which is one entry rendered
 * across ~365 city pages and so cannot carry city-specific detail. Cities with
 * no `bodyHeading` fall back to the Contentful body — see service-city.js.
 *
 * Bullet and service strings are written as "Lead-in — detail"; the lead-in is
 * split out and emphasised, matching the design and city-trusted-choice.js.
 */
const splitLead = (text) => {
  const i = text.indexOf(" — ");
  if (i === -1) return { lead: null, rest: text };
  return { lead: text.slice(0, i), rest: text.slice(i + 3) };
};

const CityBodyContent = ({ city, service }) => {
  const {
    bodyHeading,
    bodyIntro,
    whyHeading,
    whyBullets = [],
    servicesHeading,
    serviceDetails = [],
    promiseHeading,
    promiseBody,
  } = cityPageContent.forPage(city, service);

  if (!bodyHeading) return null;

  return (
    <>
      <h2 className="text-2xl md:text-3xl font-display font-extrabold text-neutral-900 leading-tight">
        {bodyHeading}
      </h2>
      {bodyIntro ? <p className="text-neutral-700 leading-relaxed">{bodyIntro}</p> : null}

      {whyHeading ? (
        <h2 className="text-2xl md:text-3xl font-display font-extrabold text-neutral-900 leading-tight mt-12">
          {whyHeading}
        </h2>
      ) : null}

      {whyBullets.length ? (
        <ul className="not-prose list-none p-0 mt-8 mb-0 space-y-4">
          {whyBullets.map((item) => {
            const { lead, rest } = splitLead(item);
            return (
              <li key={item.slice(0, 40)} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-50 flex items-center justify-center mt-0.5">
                  <Check size={14} className="text-brand-500" strokeWidth={3} />
                </span>
                <span className="text-neutral-700 leading-relaxed">
                  {lead ? (
                    <>
                      <strong className="text-neutral-900 font-semibold">{lead}</strong> — {rest}
                    </>
                  ) : (
                    rest
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      ) : null}

      {servicesHeading ? (
        <h2 className="text-2xl md:text-3xl font-display font-extrabold text-neutral-900 leading-tight mt-12">
          {servicesHeading}
        </h2>
      ) : null}

      {serviceDetails.map((item) => {
        const { lead, rest } = splitLead(item);
        return (
          <p key={item.slice(0, 40)} className="text-neutral-700 leading-relaxed">
            {lead ? (
              <>
                <strong className="text-neutral-900 font-semibold">{lead}</strong> — {rest}
              </>
            ) : (
              rest
            )}
          </p>
        );
      })}

      {promiseHeading ? (
        <h2 className="text-2xl md:text-3xl font-display font-extrabold text-neutral-900 leading-tight mt-12">
          {promiseHeading}
        </h2>
      ) : null}
      {promiseBody ? <p className="text-neutral-700 leading-relaxed">{promiseBody}</p> : null}
    </>
  );
};

export default CityBodyContent;
